import { useState, useEffect } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "./msalConfig";
import axios from "axios";

const langLabels = {
  de: { title: "Reiseplanung", subtitle: "Melden Sie sich mit Ihrem Unternehmenskonto an", btn: "Mit Microsoft anmelden", loading: "Anmelden...", error: "Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut." },
  en: { title: "Travel Planning", subtitle: "Sign in with your company account", btn: "Sign in with Microsoft", loading: "Signing in...", error: "Sign-in failed. Please try again." },
  pl: { title: "Planowanie Podróży", subtitle: "Zaloguj się kontem firmowym", btn: "Zaloguj się przez Microsoft", loading: "Logowanie...", error: "Logowanie nie powiodło się. Spróbuj ponownie." },
};

function Login({ onLogin, lang = "de", onChangeLang }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [backendLoading, setBackendLoading] = useState(false);
  const [error, setError] = useState("");
  const t = langLabels[lang] || langLabels.de;

  // Called after redirect lands back on our page — MSAL has processed the auth code
  useEffect(() => {
    if (
      isAuthenticated &&
      accounts.length > 0 &&
      inProgress === InteractionStatus.None &&
      !backendLoading
    ) {
      setBackendLoading(true);
      setError("");
      instance
        .acquireTokenSilent({ ...loginRequest, account: accounts[0] })
        .then(async (tokenResponse) => {
          try {
            const res = await axios.post(
              "/api/auth/entra",
              { idToken: tokenResponse.idToken },
              { withCredentials: true }
            );
            onLogin(res.data.user, res.data.token);
          } catch (err) {
            console.error("Backend auth error:", err);
            setError(t.error + (err.response?.data?.detail ? ` (${err.response.data.detail})` : ""));
            setBackendLoading(false);
          }
        })
        .catch((err) => {
          console.error("Token acquisition error:", err);
          setError(t.error);
          setBackendLoading(false);
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, inProgress]);

  const handleLogin = () => {
    setError("");
    instance.loginRedirect(loginRequest);
  };

  const isLoading = inProgress !== InteractionStatus.None || backendLoading;

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", backgroundColor: "#f0f4f8" }}>
        <div style={{ textAlign: "center", color: "#1a237e" }}>
          <div style={{ width: 40, height: 40, border: "4px solid #e3f2fd", borderTop: "4px solid #1565c0", borderRadius: "50%", margin: "0 auto 16px", animation: "spin 1s linear infinite" }} />
          <div style={{ fontSize: 15 }}>{t.loading}</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", backgroundColor: "#f0f4f8" }}>
      <div style={{ backgroundColor: "#fff", padding: 40, borderRadius: 12, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", width: "100%", maxWidth: 400 }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
          <div>
            <img src="/ZW_white.png" alt="ZW Logo" style={{ height: 36, marginBottom: 12, filter: "invert(1) sepia(1) saturate(5) hue-rotate(190deg)" }} onError={e => { e.target.style.display = "none"; }} />
            <h2 style={{ margin: 0, color: "#1a237e", fontSize: 22 }}>{t.title}</h2>
          </div>
          {onChangeLang && (
            <select
              value={lang}
              onChange={onChangeLang}
              style={{ padding: "5px 10px", backgroundColor: "#fff", color: "#1565c0", border: "2px solid #1565c0", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: "bold" }}
            >
              <option value="de">&#x1F1E9;&#x1F1EA; Deutsch</option>
              <option value="en">&#x1F1EC;&#x1F1E7; English</option>
              <option value="pl">&#x1F1F5;&#x1F1F1; Polski</option>
            </select>
          )}
        </div>

        <p style={{ color: "#555", marginBottom: 28, fontSize: 14 }}>{t.subtitle}</p>

        {error && (
          <div style={{ backgroundColor: "#ffebee", color: "#c62828", padding: "10px 14px", borderRadius: 6, marginBottom: 20, fontSize: 14 }}>
            {error}
          </div>
        )}

        <button
          onClick={handleLogin}
          disabled={isLoading}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            padding: "12px 16px",
            backgroundColor: isLoading ? "#e3e3e3" : "#fff",
            color: isLoading ? "#999" : "#333",
            border: "1px solid #8c8c8c",
            borderRadius: 4,
            fontSize: 15,
            fontWeight: "600",
            cursor: isLoading ? "not-allowed" : "pointer",
            boxShadow: isLoading ? "none" : "0 1px 3px rgba(0,0,0,0.15)",
            fontFamily: "'Segoe UI', sans-serif",
          }}
        >
          {!isLoading && (
            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21">
              <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
              <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
              <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
            </svg>
          )}
          {isLoading ? t.loading : t.btn}
        </button>
      </div>
    </div>
  );
}

export default Login;